import {
  getRecordingTimerSeconds,
  isRecordingTimerRunning,
} from '@/app-shell/chronicle/hooks/useChronicleSessionControl';
import { useChronicleStopRecording } from '@/app-shell/chronicle/hooks/useChronicleStopRecording';
import {
  activeRecordingTransportAtom,
  CHRONICLE_ORIGIN,
  chronicleIsPausedAtom,
  chronicleSessionIdAtom,
  routesToChronicleAtom,
  type ActiveRecordingTransport,
} from '@/atoms/chronicle';
import { offlineActiveRecordingIdAtom } from '@/atoms/offline-recording/sync';
import { useMutateStopRecording } from '@/hooks/mutation/useMutateStopRecording';
import { useMutateUpdateSessionDuration } from '@/hooks/mutation/useMutateUpdateSessionDuration';
import {
  hasPendingManualCaptureStopForSession,
  registerExitCaptureStop,
} from '@/lib/desktopExitCaptureStop';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import {
  activeSessionIdAtom,
  addStartNowStoppedSessionId,
  removeStartNowStoppedSessionId,
  startNowStoppedSessionIdsAtom,
  useMutateUpdateSessionRecordingStatus,
} from '@heidi/session/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

export function DesktopExitCaptureStopRunner() {
  const routesToChronicle = useAtomValue(routesToChronicleAtom);
  const chronicleSessionId = useAtomValue(chronicleSessionIdAtom);
  const chronicleIsPaused = useAtomValue(chronicleIsPausedAtom);
  const activeSessionId = useAtomValue(activeSessionIdAtom);
  const offlineRecordingId = useAtomValue(offlineActiveRecordingIdAtom);
  const transport = useAtomValue(activeRecordingTransportAtom);

  if (routesToChronicle && chronicleSessionId !== null) {
    return chronicleIsPaused ? null : (
      <ChronicleExitStopRunner sessionId={chronicleSessionId} />
    );
  }
  if (activeSessionId !== null) {
    return (
      <MicExitStopRunner
        sessionId={activeSessionId}
        offlineRecordingId={offlineRecordingId}
        transport={transport}
      />
    );
  }
  return null;
}

function ChronicleExitStopRunner({ sessionId }: { sessionId: string }) {
  const { stopChronicleRecording } = useChronicleStopRecording();
  const latest = useRef({ sessionId, stopChronicleRecording });
  latest.current = { sessionId, stopChronicleRecording };

  useEffect(
    () =>
      registerExitCaptureStop(async () => {
        const current = latest.current;
        if (hasPendingManualCaptureStopForSession(current.sessionId)) {
          return 'nothingToStop';
        }
        const result = await current.stopChronicleRecording({
          origin: CHRONICLE_ORIGIN.DESKTOP,
        });
        return result === 'no_session' ? 'nothingToStop' : 'stopped';
      }),
    []
  );

  return null;
}

function MicExitStopRunner({
  sessionId,
  offlineRecordingId,
  transport,
}: {
  sessionId: string;
  offlineRecordingId: string | null;
  transport: ActiveRecordingTransport | null;
}) {
  const { mutateAsync: stopRecording } = useMutateStopRecording({ sessionId });
  const { mutate: updateSessionDuration } = useMutateUpdateSessionDuration();
  const { mutate: updateRecordingStatus } = useMutateUpdateSessionRecordingStatus();
  const setStoppedSessionIds = useSetAtom(startNowStoppedSessionIdsAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const latest = useRef({
    sessionId,
    offlineRecordingId,
    transport,
    stopRecording,
    updateSessionDuration,
    updateRecordingStatus,
    setStoppedSessionIds,
    setActiveSessionId,
  });
  latest.current = {
    sessionId,
    offlineRecordingId,
    transport,
    stopRecording,
    updateSessionDuration,
    updateRecordingStatus,
    setStoppedSessionIds,
    setActiveSessionId,
  };

  useEffect(
    () =>
      registerExitCaptureStop(async () => {
        const current = latest.current;
        if (hasPendingManualCaptureStopForSession(current.sessionId)) {
          return 'nothingToStop';
        }
        if (current.offlineRecordingId !== null && current.transport !== 'desktop-mic') {
          return 'nothingToStop';
        }
        if (!isRecordingTimerRunning()) return 'nothingToStop';

        const { sessionId: currentSessionId } = current;
        current.updateRecordingStatus({
          recordingStatus: RecordingStatus.IDLE,
          sessionId: currentSessionId,
        });
        const removeStoppedSessionId = () => {
          current.setStoppedSessionIds((ids) =>
            removeStartNowStoppedSessionId(ids, currentSessionId)
          );
        };
        current.setStoppedSessionIds((ids) =>
          addStartNowStoppedSessionId(ids, currentSessionId)
        );

        try {
          current.updateSessionDuration(
            {
              sessionId: currentSessionId,
              duration: getRecordingTimerSeconds(),
            },
            { onError: removeStoppedSessionId }
          );
          await current.stopRecording({ isPaused: false });
          current.setActiveSessionId(null);
          return 'stopped';
        } catch (error) {
          removeStoppedSessionId();
          throw error;
        }
      }),
    []
  );

  return null;
}
